import React from 'react';
import { Button, Flex, Space, theme, Typography } from 'antd';
import { CheckSquareFilled, CloseOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';

const FEATHER_ICON_ACTIONS = {
  'trash-2': { icon: <DeleteOutlined />, label: 'Delete' },
  'more-vertical': { icon: <MoreOutlined />, label: 'More actions' },
};

export type CheckListAction<TName extends string = string> = {
  name: TName;
  text?: React.ReactNode;
  icon?: keyof typeof FEATHER_ICON_ACTIONS;
};

type CheckListOptionsProps<TName extends string> = {
  width?: number | string;
  count?: number;
  /** Singular noun for the selected items. */
  name?: string;
  actions?: CheckListAction<TName>[];
  onsubmit: (actionName: TName) => void;
  onclose: () => void;
  show?: boolean;
  isActive?: boolean;
};

// FIXME: no exact token for the bar's minimum width (the theme used 350px).
const MIN_WIDTH = 350;

// FIXME: no exact token for the bar's default width.
const DEFAULT_WIDTH = 400;

function CheckListOptions<TName extends string>({
  width = DEFAULT_WIDTH,
  count = 0,
  name = 'item',
  actions = [],
  onsubmit,
  onclose,
  show = true,
  isActive = true,
}: CheckListOptionsProps<TName>) {
  const { token } = theme.useToken();
  if (count === 0 || !show) return null;

  return (
    <Flex
      role="alert"
      align="center"
      justify="space-between"
      gap={token.marginSM}
      style={{
        position: 'fixed',
        bottom: token.marginLG,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: token.zIndexPopupBase,
        width,
        minWidth: MIN_WIDTH,
        padding: `${token.paddingSM}px ${token.padding}px`,
        backgroundColor: token.colorTextBase,
        color: token.colorWhite,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadow,
      }}
    >
      <Flex align="center" gap={token.marginXS}>
        <CheckSquareFilled style={{ color: token.colorPrimary, fontSize: token.fontSizeLG }} />
        <Typography.Text style={{ color: token.colorWhite }}>
          {count} {count === 1 ? name : `${name}s`}
        </Typography.Text>
      </Flex>
      <Space size={token.marginXXS}>
        {actions.map((action) => (
          <Button
            key={action.name}
            ghost
            icon={action.icon && FEATHER_ICON_ACTIONS[action.icon]?.icon}
            aria-label={action.text || !action.icon ? undefined : FEATHER_ICON_ACTIONS[action.icon]?.label}
            disabled={!isActive}
            onClick={() => onsubmit(action.name)}
          >
            {action.text}
          </Button>
        ))}
        <Button
          type="text"
          size="small"
          aria-label="Close"
          icon={<CloseOutlined />}
          style={{ color: token.colorWhite }}
          onClick={() => onclose()}
        />
      </Space>
    </Flex>
  );
}

type ControlButtonProps = {
  text?: React.ReactNode;
  onclick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  /** Any truthy value shows the loading state (legacy callers pass a spinner colour object). */
  loading?: unknown;
  color?: 'success';
  action?: 'submit';
};

const ControlButton = ({ text, onclick, disabled = false, loading, color, action }: ControlButtonProps) => (
  <Button
    type={color === 'success' ? 'primary' : 'default'}
    htmlType={action === 'submit' ? 'submit' : 'button'}
    onClick={onclick}
    disabled={disabled}
    loading={Boolean(loading)}
  >
    {text}
  </Button>
);

export { CheckListOptions, ControlButton };
